import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { phoneNumberApi } from '~/apis/phoneNumberApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { getDefaultCountryCode } from '~/utils/formatter'

import {
  PhoneNumberDetailPage,
  PhoneNumbersListPage,
} from '@mangovoice/phone-numbers-ui'
import type { PhoneNumberListItem } from '@mangovoice/phone-numbers-ui'

interface PhoneNumberSettingsPageProps {
  phoneNumberId?: string
}

export const PhoneNumberSettingsPage = ({
  phoneNumberId,
}: PhoneNumberSettingsPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })
  const checkRole = useCheckRole()

  const isStaff = userRoles?.is_staff ?? false
  const isSuperuser = userRoles?.is_superuser ?? false
  const canEdit = checkRole('admin')
  const canDelete = checkRole('admin')
  const canCreate = isStaff || isSuperuser

  if (!pbxId) {
    return null
  }

  const basePath = `${ApplicationRoutes.SETTINGS}/phone-numbers`

  const handleRowClick = (pn: PhoneNumberListItem) => {
    navigate(`${basePath}/${pn.id}`)
  }

  if (phoneNumberId) {
    return (
      <Box p={6} overflow={'auto'}>
        <PhoneNumberDetailPage
          api={phoneNumberApi}
          phoneNumberId={Number(phoneNumberId)}
          listPath={basePath}
          t={t}
          toast={toaster}
          isStaff={isStaff}
          isSuperuser={isSuperuser}
          bandwidthAccountId={import.meta.env.VITE_BANDWIDTH_ACCOUNT_ID}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </Box>
    )
  }

  return (
    <Box p={6} overflow={'auto'}>
      <PhoneNumbersListPage
        api={phoneNumberApi}
        pbxId={pbxId}
        onRowClick={handleRowClick}
        t={t}
        toast={toaster}
        canCreate={canCreate}
        canEdit={canEdit}
        defaultCountryCode={getDefaultCountryCode()}
      />
    </Box>
  )
}
